import * as React from "react";
import { useQueryClient } from "react-query";
import { useOktaAuth } from "@okta/okta-react";
import { useAsync } from "../utils/asyncHooks";
//import { fetchUserDataOkta } from "../helpers/fetchUserDataOkta";
import { fetchUserData } from "../helpers/fetchUserData";
import FullPageSpinner from "../screens/components/elements/FullPageSpinner";
import FiveHundredsError from "../screens/components/elements/FiveHundredsError";
import parseErrorMessagesList from "../helpers/parseErrorMessagesList";

const AuthContext = React.createContext();
AuthContext.displayName = "AuthContext";

function AuthProvider(props) {
  const {
    data: userInfo,
    status,
    error,
    isLoading,
    isIdle,
    isError,
    isSuccess,
    run,
  } = useAsync();

  const { authState, oktaAuth } = useOktaAuth();

  const queryClient = useQueryClient();

  // this is passed on to children
  const [isLoggedIn, setIsLoggedIn] = React.useState(null);

  const logout = React.useCallback(async () => {
    await oktaAuth.signOut("/");
    queryClient.clear();
  }, [oktaAuth, queryClient]);

  React.useEffect(() => {
    if (!authState?.isAuthenticated) {
      setIsLoggedIn(null);
      return;
    }
    const curState = oktaAuth?.authStateManager?.getAuthState();
    const prevState = oktaAuth?.authStateManager?.getPreviousAuthState();
    if (
      prevState?.isAuthenticated === true &&
      curState?.isAuthenticated === true
    ) {
      return;
    }
    setIsLoggedIn(true);
    //const userDataPromise = oktaAuth.token.getUserInfo();
    const userDataPromise = fetchUserData(
      queryClient,
      oktaAuth.getAccessToken()
    );
    run(userDataPromise);
  }, [authState, oktaAuth, run, queryClient]);

  React.useEffect(() => {
    if (!isLoggedIn && userInfo) {
      logout();
    }
  }, [isLoggedIn, userInfo, logout]);

  const login = React.useCallback(async () => {
    oktaAuth.signInWithRedirect({ originalUri: "/" });
  }, [oktaAuth]);

  //update greeting
  const [greeting, setGreeting] = React.useState("");
  React.useEffect(() => {
    if (!greeting) {
      return;
    }
    setGreeting(greeting);
  }, [greeting]);

  const value = React.useMemo(
    () => ({ userInfo, isLoggedIn, logout, greeting, setGreeting, login }),
    [userInfo, isLoggedIn, logout, greeting, setGreeting, login]
  );

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (isError) {
    let errMessage = error
      ? parseErrorMessagesList(error)
      : "Please try again later";
    return <FiveHundredsError message={errMessage} />;
  }

  if (isSuccess || isIdle) {
    //let fullValue = { ...value, ...props };
    return <AuthContext.Provider value={value} {...props} />;
  }

  throw new Error(`Unhandled status: ${status}`);
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}
// client with token for authorized calls
// function useClient() {
//   const { oktaToken } = useAuth();
//   const token = oktaToken ? oktaToken : null;
//   return React.useCallback(
//     (endpoint, config) => client(endpoint, { ...config, token }),
//     [token]
//   );
// }

export { AuthProvider, useAuth };
